import {Inject, Injectable} from '@angular/core';
import {PageStateService} from './page-state.service';
import {SignupTarget} from '../enums/signup-target.enum';
import {PageState} from '../enums/page-state.enum';
import {MessagesService} from './messages.service';
import {MessageType} from '../enums/message-type';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {first} from 'rxjs';
import {parseJSendException} from '../../lib/jsend/operators/parse-jsend-exception.operator';
import {ExternalValidationException} from '../../lib/jsend/exceptions/external-validation.exception';
import {ExternalErrorException} from '../../lib/jsend/exceptions/external-error.exception';
import {CompoundedWindowType} from '../types/compounded-window.type';

@Injectable({
  providedIn: 'root'
})
export class EmbeddedSignupService {
  public constructor(
    private readonly pageStateService: PageStateService,
    private readonly messagesService: MessagesService,
    private readonly httpClient: HttpClient,
    @Inject(Window) private readonly window: CompoundedWindowType
  ) {
  }

  public launchInstagramSignup(event: Event): void {
    this.launchSignup(
      SignupTarget.INSTAGRAM,
      event,
      'general',
      'api/signup/instagram'
    );
  }

  public launchMessengerSignup(event: Event): void {
    this.launchSignup(
      SignupTarget.MESSENGER,
      event,
      'general',
      'api/signup/messenger'
    );
  }

  public launchWhatsAppSignup(event: Event): void {
    this.launchSignup(
      SignupTarget.WHATSAPP,
      event,
      'whatsapp_embedded_signup',
      'api/signup/whatsapp'
    );
  }

  public launchSignup(
    target: SignupTarget,
    event: Event,
    feature: string,
    endpoint: string
  ): void {
    this.window.fbq('trackCustom', event.type, {
      appId: this.window["appId"],
      feature: feature,
      target: target.toString()
    });
    this.messagesService.clearMessages();

    if (!this.window["oneTimePassword"]) {
      this.messagesService.addMessage({
        type: MessageType.ERROR,
        message: 'Configuration invalid, please contact support.'
      });
      return;
    }

    this.pageStateService.setState(PageState.WaitingForMeta);

    // If you are logged in, automatically get your name and email address, your public profile information
    this.window["FB"].login((response) => {
      if (!response.hasOwnProperty('authResponse') || !response.authResponse) {
        this.pageStateService.setState(PageState.CancelledByUser);
        return;
      }

      if (!response.authResponse.hasOwnProperty('code') || !response.authResponse.code) {
        this.pageStateService.setState(PageState.CancelledByUser);
        return;
      }

      this.handleEmbeddedSignup(response.authResponse.code, endpoint);
    }, {
      config_id: this.window["configurationId"],
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        sessionInfoVersion: 2
      }
    });
  }

  public handleEmbeddedSignup(
    authenticationCode: string,
    endpoint: string
  ): void {
    const oneTimePassword = this.window["oneTimePassword"];
    const customerFacingWebsiteUrl = this.window["customerFacingWebsiteUrl"];

    this.pageStateService.setState(PageState.WaitingForApi);

    this.httpClient.post(
      `${customerFacingWebsiteUrl}/${endpoint}`,
      {code: authenticationCode, otp: oneTimePassword},
      {
        headers: new HttpHeaders({
          'content-type': 'application/json',
          'accept': 'application/json'
        })
      }
    ).pipe(first(), parseJSendException()).subscribe({
      next: responseData => {
        this.pageStateService.setState(PageState.Done);

        if (responseData && 'redirect' in responseData && responseData.redirect
          && 'location' in responseData
          && typeof responseData.location === 'string') {
          this.window.location.assign(responseData.location);
          return;
        }

        this.window.location.assign('/signup-completed');
      },
      error: (err: Error) => {
        this.pageStateService.setState(PageState.Error);

        if (err instanceof ExternalValidationException) {
          Object.entries(err.getFailedFields()).forEach((failedField) => {
            const fieldName = failedField[0];
            const messages = failedField[1];

            Object.values(messages).forEach(errorMessage => {
              this.messagesService.addMessage({type: MessageType.ERROR, message: `${fieldName}: ${errorMessage}`});
            });
          });
        } else if (err instanceof ExternalErrorException) {
          this.messagesService.addMessage({type: MessageType.ERROR, message: err.getExternalMessage()});
        } else {
          this.messagesService.addMessage({type: MessageType.ERROR, message: err.message});
        }
      }
    });
  }
}
